import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, useContext, useEffect } from "react";
import { createPortal } from "react-dom";
import classNames from "classnames";
import ButtonListContext from "@widget/buttons/ButtonListContext";
import useIsMobileLayout from "@hooks/useIsMobileLayout";
/** Phones: where a tab's header buttons go (the tab slider), set by TabComponent */
export const TabActionsSlotContext = createContext(undefined);
/** Phones: tells the slot's owner that a tab header puts buttons there (+1 / -1), so it shows its bar */
export const TabActionsCountContext = createContext(undefined);
const ICON_MODE = { icon: true };
/**
 * @export
 * The header at the top of a tab's content: a title with the tab's buttons on the right.
 * On phones the tab slider already names the tab, so the title hides and the buttons move into
 * the slider as icons (a button shows its icon, its title becomes the tooltip). No phone code
 * is needed in the app.
 *
 * @example
 * ```tsx
 * <TabComponent tabs={[{ id: 'keys', label: 'API keys', content: <>
 *     <TabSectionHeader title="My API keys" actions={<Button title="Generate key" leftIcon="fal key" onClick={generate} />} />
 *     <KeysTable />
 * </> }]} selected={tab} onChangeTab={setTab} />
 * ```
 *
 * @category Navigation
 */
const TabSectionHeader = ({ title, actions, section, className }) => {
    const slot = useContext(TabActionsSlotContext);
    const countButtons = useContext(TabActionsCountContext);
    const isMobile = useIsMobileLayout();
    const inSlot = slot !== undefined && !section;
    // Let the slot's owner (a details panel) know it has buttons to show
    useEffect(() => {
        if (!inSlot || !actions || !countButtons)
            return;
        countButtons(1);
        return () => countButtons(-1);
    }, [inSlot, !!actions, countButtons]);
    if (section) {
        return _jsxs("div", { className: classNames("uxpcore_tabsectionheader uxpcore_tabsectionheader--section", className), children: [_jsx("div", { className: "uxpcore_tabsectionheader__title", children: title }), actions && _jsx("div", { className: "uxpcore_tabsectionheader__actions", children: isMobile ? _jsx(ButtonListContext.Provider, { value: ICON_MODE, children: actions }) : actions })] });
    }
    // Inside a phone tab slider or details panel toolbar: no title row; the buttons render there
    if (inSlot) {
        return slot && actions
            ? createPortal(_jsx(ButtonListContext.Provider, { value: ICON_MODE, children: actions }), slot)
            : null;
    }
    return _jsxs("div", { className: classNames("uxpcore_tabsectionheader", className), children: [_jsx("div", { className: "uxpcore_tabsectionheader__title", children: title }), actions && _jsx("div", { className: "uxpcore_tabsectionheader__actions", children: actions })] });
};
export default TabSectionHeader;
